import React, { useEffect, useState } from 'react';
import { fetchSupportAnalyticsAPI, fetchServersAPI } from '../services/serverService';
import { FaChartLine, FaStar, FaUserCheck, FaSpinner, FaExternalLinkAlt, FaClock, FaCheckCircle, FaListUl } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function SupportStatsPage() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Filters state
    const [dateRange, setDateRange] = useState('30');
    const [serverFilter, setServerFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Servers list for filter
    const [serversList, setServersList] = useState([]);

    useEffect(() => {
        const fetchServers = async () => {
            try {
                const res = await fetchServersAPI();
                setServersList(res.data?.data?.servers || []);
            } catch (err) {
                console.error('Failed to load servers', err);
            }
        };
        fetchServers();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const filters = {
                    dateRange,
                    serverId: serverFilter !== 'all' ? serverFilter : null,
                    category: categoryFilter !== 'all' ? categoryFilter : null
                };
                const res = await fetchSupportAnalyticsAPI(filters);
                setData(res.data.data);
            } catch (err) {
                setError('Failed to load analytics.');
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [dateRange, serverFilter, categoryFilter]);

    if (isLoading && !data) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#3182ce', fontSize: '24px' }}><FaSpinner className="fa-spin" /></div>;
    if (error) return <div style={{ padding: '40px', color: 'red', textAlign: 'center' }}>{error}</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f7fa', minHeight: '100vh', fontFamily: 'sans-serif' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Support Analytics</h1>
                    <p style={{ margin: 0, color: '#718096' }}>Monitor team performance and customer satisfaction.</p>
                </div>

                {/* Filters */}
                <div style={{ display: 'flex', gap: '15px', backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Date Range</label>
                        <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', cursor: 'pointer' }}>
                            <option value="1">Today</option>
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 90 Days</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Server</label>
                        <select value={serverFilter} onChange={e => setServerFilter(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', cursor: 'pointer' }}>
                            <option value="all">All Servers</option>
                            {serversList.map((server, idx) => (
                                <option key={`${server.id || server.server_id}-${idx}`} value={server.id || server.server_id}>{server.name || server.server_name || server.id || server.server_id}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Category</label>
                        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', cursor: 'pointer' }}>
                            <option value="all">All Categories</option>
                            <option value="Bug/Crash">Bug/Crash</option>
                            <option value="Feature Request">Feature Request</option>
                            <option value="Billing/Invoice">Billing/Invoice</option>
                            <option value="General Query">General Query</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                </div>
            </div>

            {isLoading && data && <div style={{ position: 'fixed', top: 20, right: 20, color: '#3182ce', backgroundColor: 'white', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}><FaSpinner className="fa-spin" style={{ marginRight: '8px' }} /> Loading...</div>}

            {/* Top Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: '4px solid #3182ce' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <FaChartLine /> Total Tickets
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748' }}>{data?.totalTickets || 0}</div>
                </div>

                <div
                    onClick={() => navigate('/admin/support/metrics-detail?type=csat')}
                    style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderLeft: '4px solid #d69e2e', cursor: 'pointer', transition: 'transform 0.2s', position: 'relative' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaStar /> Global CSAT</span>
                        <FaExternalLinkAlt style={{ fontSize: '12px', color: '#a0aec0' }} />
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        {data?.globalCSAT || '0.0'} <span style={{ fontSize: '16px', color: '#a0aec0', fontWeight: 'normal' }}>/ 5.0</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#3182ce', marginTop: '10px', fontWeight: 'bold' }}>View Rated Tickets →</div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: '4px solid #805ad5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <FaClock /> Avg First Response
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748' }}>{data?.avgFirstResponseTime || '0h'}</div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: '4px solid #38a169' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <FaCheckCircle /> Avg Resolution Time
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748' }}>{data?.avgResolutionTime || '0h'}</div>
                </div>
            </div>

            {/* Charts Area */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#4a5568' }}>Ticket Resolution Trends</h3>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data?.trends || []}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f7" />
                                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#a0aec0' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 12, fill: '#a0aec0' }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                <Line type="monotone" dataKey="created" name="New Tickets" stroke="#e53e3e" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                                <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#38a169" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', color: '#4a5568', fontWeight: 'bold' }}>
                        <FaListUl /> Tickets by Category
                    </div>
                    <div style={{ width: '100%', height: '280px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data?.categoryBreakdown || []} layout="vertical" margin={{ top: 0, right: 0, left: 20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf2f7" />
                                <XAxis type="number" tick={{ fontSize: 12, fill: '#a0aec0' }} axisLine={false} tickLine={false} />
                                <YAxis dataKey="category" type="category" tick={{ fontSize: 12, fill: '#4a5568' }} axisLine={false} tickLine={false} width={80} />
                                <Tooltip cursor={{ fill: '#f7fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                                <Bar dataKey="count" fill="#3182ce" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Agent Performance Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #edf2f7', backgroundColor: '#f7fafc', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaUserCheck style={{ color: '#4a5568' }} />
                    <h3 style={{ margin: 0, color: '#4a5568', fontSize: '16px' }}>Agent Performance & QA (Time Scoped)</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#fff', borderBottom: '2px solid #edf2f7', color: '#718096' }}>
                                <th style={{ padding: '15px' }}>Agent Name</th>
                                <th style={{ padding: '15px' }}>Total Handled</th>
                                <th style={{ padding: '15px' }}>Currently Open</th>
                                <th style={{ padding: '15px' }}>QA Follow-ups</th>
                                <th style={{ padding: '15px' }}>Avg CSAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.agentStats?.map((agent, index) => (
                                <tr
                                    key={index}
                                    onClick={() => {
                                        const routeUid = agent.uid === 'unassigned' ? 'Unassigned' : agent.uid;
                                        navigate(`/support?assignee=${routeUid}`);
                                    }}
                                    style={{ borderBottom: '1px solid #edf2f7', cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ebf8ff'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#2b6cb0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {agent.name} <FaExternalLinkAlt style={{ fontSize: '10px', color: '#a0aec0' }} />
                                    </td>
                                    <td style={{ padding: '15px' }}>{agent.totalHandled}</td>
                                    <td style={{ padding: '15px', color: '#3182ce' }}>{agent.open}</td>
                                    <td style={{ padding: '15px' }}>
                                        {agent.followUpsPending > 0 ? (
                                            <span style={{ backgroundColor: '#fed7d7', color: '#c53030', padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold', fontSize: '12px' }}>{agent.followUpsPending} Pending</span>
                                        ) : (
                                            <span style={{ color: '#a0aec0', fontSize: '12px' }}>0</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '15px' }}>
                                        {agent.avgCSAT > 0 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: agent.avgCSAT >= 4 ? '#d69e2e' : '#e53e3e', fontWeight: 'bold' }}>
                                                {agent.avgCSAT} <FaStar />
                                            </div>
                                        ) : <span style={{ color: '#a0aec0', fontSize: '12px' }}>No Data</span>}
                                    </td>
                                </tr>
                            ))}
                            {(!data?.agentStats || data.agentStats.length === 0) && (
                                <tr>
                                    <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>No agent data found for this period.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}